<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import BasePage from "@/components/Layouts/BasePage.vue";
import BaseTable from "@/components/tables/BaseTable.vue";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import BaseAdvancedFilter from "@/components/common/Filters/BaseAdvancedFilter.vue";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import { CollaboratorEntity } from "@/entities/CollaboratorEntity.js";
import { deleteCollaborator, getCollaborators } from "@/services/api/collaborators.js";
import { getAvailableActionsForRoute } from "@/services/api/permissionManagement.js";
import { handleError } from "@/utils/errors.js";
import { computeClass } from "@/utils/styleFormats.js";
import { dateFormatterInput } from "@/utils/dateTimeFormat.js";

const { t } = useI18n();
const router = useRouter();
const entity = ref(new CollaboratorEntity());

const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const filters = ref([]);
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref([]);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null);
const orderBy = ref(null);

function isActiveStatus(status) {
    return status === "ativo" || status === true || status === "active";
}

async function fetchData(params = {}) {
    try {
        loading.value = true;
        currentPage.value = params.page ?? currentPage.value;

        if (orderBy.value) {
            params.orderBy = orderBy.value.key;
        }

        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0];
            params[field] = "%" + searchText.value + "%";
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params
            };
        }

        const response = await getCollaborators({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params
        });

        const responseItems = response.data?.items ?? response.data ?? [];
        items.value = Array.isArray(responseItems)
            ? responseItems.map(item => ({
                ...item,
                data_admissao: dateFormatterInput(item.data_admissao),
            }))
            : responseItems;
        totalItems.value = response.page;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

function onEdit(item) {
    router.push(entity.value.getUrl(entity.value.pages.edit.route?.at(-1), { id: item.id }));
}

function onView(item) {
    router.push(entity.value.getUrl(entity.value.pages.view.route?.at(-1), { id: item.id }));
}

async function onPageChange(page) {
    await fetchData({ page });
}

async function onDelete(item) {
    itemToDelete.value = item;
    showDialogConfirmDelet.value = true;
}

async function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced;
    await fetchData(filtersAdvanced);
}

async function onCleanFilter() {
    filters.value = null;
    await fetchData();
}

async function onOrderBy(orderByArray) {
    orderBy.value = orderByArray;
}

const confirmDelete = async () => {
    try {
        await deleteCollaborator({ id: itemToDelete.value.id });
        await fetchData();
        message.success(t("common.messages.success.itemDeletedSuccessfully"));
        showDialogConfirmDelet.value = false;
    } catch (error) {
        message.error(await handleError(error, t));
    }
};

onMounted(async () => {
    actions.value = await getAvailableActionsForRoute(entity.value.APIRoute);
    if (!actions.value?.list?.length) {
        message.error(t("common.messages.errors.403"));
        return;
    }

    await fetchData();
});
</script>

<template>
    <BasePage :entity="entity">
        <template #default>
            <v-row class="mb-4">
                <v-col>
                    <ButtonNew :route="entity.pages.create.route?.at(-1)" v-if="actions && actions.create" />
                </v-col>
                <v-col class="base-advanced-filter">
                    <BaseAdvancedFilter
                        :entity="entity"
                        @filter="onAdvancedFilter"
                        @clean="onCleanFilter"
                    />
                </v-col>
                <v-col class="base-quick-search">
                    <BaseQuickSearch
                        :entity="entity"
                        v-if="quickSearchConfig"
                        :config="quickSearchConfig"
                        v-model="searchText"
                        :fetchData="fetchData"
                    />
                </v-col>
            </v-row>

            <BaseTable
                :fetchData="fetchData"
                :entity="entity"
                :items="items"
                :loading="loading"
                :totalItems="totalItems ?? 0"
                :currentPage="currentPage"
                :itemsPerPage="itemsPerPage"
                :actions="actions"
                @edit="onEdit"
                @view="onView"
                @delete="onDelete"
                @page-change="onPageChange"
                @onOrderBy="onOrderBy"
            >
                <template #status="{ row }">
                    <span class="trezo-status-badge" :class="computeClass(isActiveStatus(row.status))">
                        {{ t("common.labels." + (isActiveStatus(row.status) ? "active" : "inactive")) }}
                    </span>
                </template>
            </BaseTable>

            <DialogConfirmDelet
                v-model="showDialogConfirmDelet"
                :item="itemToDelete?.nome"
                @confirm="confirmDelete"
            />
        </template>
    </BasePage>
</template>
